/* eslint-disable react/prop-types */
import { Button, CrashIcon, EmptyState, NoDataIcon } from '@lmc/ui-kit'
import { getJSON } from 'common/services/api'
import dayjs, { type Dayjs } from 'dayjs'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/reports'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { LoadingScreen } from '../../../../components/generic'
import chartTitles from './chartTitles'
import withToolbar from './withToolbar'

type DateRange = {
  from?: Dayjs | null
  to?: Dayjs | null
}

const getDefaultRange = (timeWindow?: number): DateRange =>
  timeWindow
    ? {
        from: dayjs().subtract(timeWindow, 'days').startOf('day'),
        to: dayjs().endOf('day')
      }
    : {}

// HOC is used by ResidentChartScreen to render individual charts
type WithDataLoaderProps = {
  enableMockData: boolean
  errorMessage: string
  url: (props: any) => string
  timeWindow?: number
}

type DataLoaderProps = {
  mockDataFetch: any
  params: {
    resident_id: string
    chart_type: string
  }
  from?: Dayjs | null
  to?: Dayjs | null
}

type WrappedComponentProps = {
  mock: boolean
  data: any
  from?: Dayjs | null
  to?: Dayjs | null
  onDatesChange?: (range: DateRange) => void
}

export default function withDataLoader(
  WrappedComponent: (props: WrappedComponentProps) => JSX.Element,
  { enableMockData, errorMessage, url, timeWindow }: WithDataLoaderProps
) {
  const DataLoader = (props: DataLoaderProps) => {
    const [shouldRenderMockData, setShouldRenderMockData] = useState(false)
    const [range, setRange] = useState<DateRange>(() =>
      getDefaultRange(timeWindow)
    )
    const fetchUrl = url({ ...props, ...range })
    const chartType = props.params?.chart_type
    // Only offer a date picker on the empty state when this loader owns the
    // range; callers that pass from/to render their own picker.
    const NoLogsChart = useMemo(
      () => buildNoLogsChart(chartType, Boolean(timeWindow)),
      [chartType]
    )

    const handleDatesChange = (newRange: DateRange) => {
      setShouldRenderMockData(false)
      setRange(
        newRange.from == null && newRange.to == null
          ? getDefaultRange(timeWindow)
          : newRange
      )
    }

    const { data, isLoading, error } = useQuery<
      { result: any },
      { message: any }
    >([fetchUrl], () => getJSON({ url: fetchUrl }), {
      cacheTime: 0
    })

    const base = url(props)
    const symb = base.match(/\?/) ? '&' : '?'
    const mockUrl = `${base}${symb}mock=1`

    const { data: mockedData, isLoading: isMockLoading } = useQuery<{
      result: any
    }>([mockUrl], () => getJSON({ url: mockUrl }), {
      enabled: shouldRenderMockData
    })

    const renderError = () => (
      <EmptyState
        title={error?.message || errorMessage || 'Oops.. Something went wrong'}
        icon={<NoDataIcon />}
      />
    )

    const renderSuccess = (paramData: any, isMock?: any) => (
      <WrappedComponent
        {...props}
        mock={isMock}
        data={paramData || data?.result || []}
        from={props.from ?? range.from}
        to={props.to ?? range.to}
        onDatesChange={handleDatesChange}
      />
    )

    if (isLoading || isMockLoading) {
      return <LoadingScreen />
    }

    if (enableMockData && shouldRenderMockData && mockedData) {
      return renderSuccess(mockedData.result, true)
    }

    if (data?.result) {
      const result = data?.result
      if (!result?.length) {
        const from = props.from ?? range.from
        const to = props.to ?? range.to
        return (
          <NoLogsChart
            {...props}
            data={[]}
            from={from}
            to={to}
            onDatesChange={handleDatesChange}
            inRange={Boolean(from || to)}
            onShowSample={
              enableMockData ? () => setShouldRenderMockData(true) : undefined
            }
          />
        )
      }

      return renderSuccess(result)
    }

    if (error) {
      return renderError()
    }

    return (
      <EmptyState icon={<CrashIcon />} title="Oops.. Something went wrong" />
    )
  }

  return DataLoader
}

type NoLogsProps = {
  inRange: boolean
  onShowSample?: () => void
}

const NoLogs = ({ inRange, onShowSample }: NoLogsProps) => {
  const { t } = useTranslation(NAMESPACE.REPORTS)
  return (
    <EmptyState
      title={t(
        inRange
          ? reportsConstants.CHART_NO_LOGS_IN_RANGE_TITLE
          : reportsConstants.CHART_NO_LOGS_TITLE
      )}
      description={
        inRange ? t(reportsConstants.CHART_NO_LOGS_IN_RANGE_DESC) : undefined
      }
      icon={<NoDataIcon />}
    >
      {onShowSample && (
        <Button onClick={onShowSample}>
          {t(reportsConstants.CHART_SHOW_SAMPLE)}
        </Button>
      )}
    </EmptyState>
  )
}

// TODO: all of HOC logic needs to be removed
const buildNoLogsChart = (
  chartType: string | undefined,
  withDateFilter: boolean
) =>
  withToolbar(NoLogs, {
    hideLogFilter: !withDateFilter,
    title: chartTitles[chartType as keyof typeof chartTitles] || 'Chart'
  })
